import type { ObjectId } from '@freightview/object-id'
import type { GenericSchema, ObjectEntries } from 'valibot'

import {
  vArray,
  vBoolean,
  vCoerceArray,
  vDateRange,
  vInt,
  vLiteral,
  vMinValue,
  vNonEmptyString,
  vNumber,
  vObject,
  vObjectId,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
  vVariant,
} from '../validation/index.ts'
import { analyticsSearchSchema } from './analytics.ts'
import {
  dashboardTileFilterSchema,
  readyCompletedStatuses,
} from './shipment-filters.ts'

export type TileGrid = {
  row: number
  col: number
  width: number
  height: number
}

export const sumMetricFields = ['spend'] as const
export type SumMetricField = (typeof sumMetricFields)[number]

export const chartGroupByOptions = [
  'carrier',
  'tag',
  'mode',
  'auditStatus',
] as const
export type ChartGroupBy = (typeof chartGroupByOptions)[number]

export const chartDisplayOptions = ['bar', 'pie', 'table'] as const
export type ChartDisplay = (typeof chartDisplayOptions)[number]

export const dashboardTileTypes = [
  'agenda',
  'assistant',
  'latest-updates',
  'overview-report',
  'metric',
  'chart',
  'messages',
] as const
export type DashboardTileType = (typeof dashboardTileTypes)[number]

const positiveInt = () => vPipe(vNumber(), vInt(), vMinValue(1))

export const tileGridSchema = vObject({
  row: positiveInt(),
  col: positiveInt(),
  width: positiveInt(),
  height: positiveInt(),
})

const layoutSchema = vObject({
  columns: positiveInt(),
  rows: vOptional(positiveInt()),
})

const tileUpsertSchema = <
  T extends DashboardTileType,
  E extends ObjectEntries = Record<never, never>,
>(
  type: T,
  additionalEntries: E = {} as E,
) =>
  vObject({
    _id: vOptional(vObjectId()),
    type: vLiteral(type),
    grid: tileGridSchema,
    locked: vOptional(vBoolean()),
    ...additionalEntries,
  })

const metricSchema = vVariant('kind', [
  vObject({ kind: vLiteral('count') }),
  vObject({
    kind: vLiteral('sum'),
    field: vPicklist(sumMetricFields),
  }),
])

const tileFilterSchema = vObject({
  savedViewId: vOptional(vString()),
  pickupDate: vOptional(vDateRange()),
})

const analyticTileSchema = vObject({
  filter: tileFilterSchema,
  metric: metricSchema,
  label: vOptional(vString()),
})

const metricConfigSchema = vObject({
  ...analyticTileSchema.entries,
})

const chartConfigSchema = vObject({
  ...analyticTileSchema.entries,
  groupBy: vPicklist(chartGroupByOptions),
  display: vPicklist(chartDisplayOptions),
  limit: vOptional(positiveInt()),
})

// const agendaTileSchema = tileUpsertSchema('agenda')
const aiAssistantTileSchema = tileUpsertSchema('assistant')
const latestUpdatesTileSchema = tileUpsertSchema('latest-updates')
const overviewReportTileSchema = tileUpsertSchema('overview-report')
const messagesTileSchema = tileUpsertSchema('messages')

const metricTileSchema = tileUpsertSchema('metric', {
  config: metricConfigSchema,
})

const chartTileSchema = tileUpsertSchema('chart', {
  config: chartConfigSchema,
})

export const tileSchema = vVariant('type', [
  // agendaTileSchema,
  aiAssistantTileSchema,
  latestUpdatesTileSchema,
  overviewReportTileSchema,
  messagesTileSchema,
  metricTileSchema,
  chartTileSchema,
])

type WithObjectId<T> = Omit<T, '_id'> & { _id: ObjectId }
type TileType<TSchema extends GenericSchema> = WithObjectId<vOutput<TSchema>>

export type Metric = vOutput<typeof metricSchema>
export type TileFilter = vOutput<typeof tileFilterSchema>
export type MetricTileConfig = vOutput<typeof metricConfigSchema>
export type ChartTileConfig = vOutput<typeof chartConfigSchema>

// export type AgendaTile = TileType<typeof agendaTileSchema>
export type AiAssistantTile = TileType<typeof aiAssistantTileSchema>
export type LatestUpdatesTile = TileType<typeof latestUpdatesTileSchema>
export type OverviewReportTile = TileType<typeof overviewReportTileSchema>
export type MetricTile = TileType<typeof metricTileSchema>
export type ChartTile = TileType<typeof chartTileSchema>
export type MessagesTile = TileType<typeof messagesTileSchema>

export type DashboardTile =
  // | AgendaTile
  | AiAssistantTile
  | LatestUpdatesTile
  | OverviewReportTile
  | MessagesTile
  | MetricTile
  | ChartTile

export type Dashboard = {
  _id: ObjectId
  userId: ObjectId
  accountId: ObjectId
  name: string
  isDefault: boolean
  layout: { columns: number; rows?: number }
  tiles: DashboardTile[]
  createdAt: Date
  updatedAt: Date
  removedDate?: Date
}

export type DashboardTileUpsertSchema = vOutput<typeof tileSchema>

export const upsertDashboardSchema = vObject({
  name: vNonEmptyString(),
  isDefault: vOptional(vBoolean()),
  layout: layoutSchema,
  tiles: vArray(tileSchema),
})

export type UpsertDashboardDTO = vOutput<typeof upsertDashboardSchema>

const analyticsFilterEntries = {
  ...analyticsSearchSchema.entries,
  pickupDate: vOptional(analyticsSearchSchema.entries.pickupDate),
}

const dashboardAggregateFilterSources = ['analytics', 'confirmed'] as const
export type DashboardAggregateFilterSource =
  (typeof dashboardAggregateFilterSources)[number]

const confirmedFilterEntries = {
  ...dashboardTileFilterSchema.entries,
  status: vOptional(vCoerceArray(vPicklist(readyCompletedStatuses)), [
    ...readyCompletedStatuses,
  ]),
}

export const dashboardAggregateFilterSchema = vVariant('source', [
  vObject({
    source: vLiteral('analytics'),
    ...analyticsFilterEntries,
  }),
  vObject({
    source: vLiteral('confirmed'),
    ...confirmedFilterEntries,
  }),
])

export type DashboardAggregateFilterDTO = vOutput<
  typeof dashboardAggregateFilterSchema
>

export const dashboardAggregateRequestSchema = vVariant('type', [
  vObject({
    type: vLiteral('metric'),
    filter: dashboardAggregateFilterSchema,
    metric: metricSchema,
  }),
  vObject({
    type: vLiteral('chart'),
    filter: dashboardAggregateFilterSchema,
    groupBy: vPicklist(chartGroupByOptions),
    metric: metricSchema,
    limit: vOptional(positiveInt()),
  }),
])

export type DashboardAggregateRequestDTO = vOutput<
  typeof dashboardAggregateRequestSchema
>
